/**
 * Win celebration animation — canvas-rendered card cascade + confetti.
 *
 * Classic Windows Solitaire-style: cards launch from foundations with random
 * velocity, fall under gravity, and bounce off screen edges leaving trails.
 * Confetti particles shoot from bottom corners.
 *
 * Architecture:
 *   - Self-contained RAF loop (like AnimationManagerClass)
 *   - Does NOT use the card position overlay system — manages its own rendering
 *   - paint.ts calls drawWinAnimation() in the Completed branch
 *   - startWinAnimation() is called once when the game transitions to Completed
 *   - stopWinAnimation() cleans up when starting a new game
 */

import { appState, getCurrentCardState } from './state'
import { drawCardAt, CARD_FONT } from './canvasMethods'
import { Card } from '../Models/Card'
import { parseColorToRGB } from './colorUtils'
import { foundationCardX, foundationCardY } from './allXYCoordinates'
import { drawNewGameButton, drawRecordsToggleButton } from './drawButtons'
import { drawCompletionTimer } from './drawTimer'
import { drawCompletionBackdrop, drawRecords } from './drawRecords'
import { schedulePaint } from './paint'

// ─── Types ──────────────────────────────────────────────────────────────────

/** A card in flight during the cascade animation. */
interface CascadeCard {
    card: Card
    x: number
    y: number
    vx: number // horizontal velocity (px/frame at 60fps)
    vy: number // vertical velocity (px/frame at 60fps)
    rotation: number // current rotation (radians)
    vr: number // rotational velocity (radians/frame)
    frameCount: number // frames since launch (for trail spacing)
}

/** A confetti particle. */
interface ConfettiParticle {
    x: number
    y: number
    vx: number
    vy: number
    width: number
    height: number
    color: string
    rotation: number
    vr: number
    opacity: number
    gravity: number
}

// ─── Module state ───────────────────────────────────────────────────────────

let _running = false
let _finished = false // True after animation naturally completes (trail stamps preserved)
let _rafId: number | null = null
let _startTime = 0

// Card cascade state
let _cascadeCards: CascadeCard[] = []
let _trailCanvas: HTMLCanvasElement | null = null // Offscreen buffer for permanent stamps
let _trailCtx: CanvasRenderingContext2D | null = null
let _globalFrame = 0 // Frame counter for stamp spacing
let _cardQueue: { card: Card; foundationIndex: number }[] = []
let _lastLaunchTime = 0
const LAUNCH_INTERVAL = 100 // ms between card launches

// Confetti state
let _confettiParticles: ConfettiParticle[] = []
let _lastConfettiBurst = 0
const CONFETTI_BURST_INTERVAL = 150 // ms between bursts
const TRAIL_FADE_ALPHA = 0.012 // per-frame opacity reduction on trail canvas (~4s full fade)

// Bob Ross easter egg
let _showBobRoss = false

// Text fade-in
const TEXT_FADE_START = 1500 // ms after start to begin fading in text
const TEXT_FADE_DURATION = 800 // ms to fully fade in

// ─── Public API ─────────────────────────────────────────────────────────────

/** Whether the win animation is currently running its RAF loop. */
export function isWinAnimationRunning(): boolean {
    return _running
}

/** Whether the Bob Ross easter egg was triggered. */
export function shouldShowBobRoss(): boolean {
    return _showBobRoss
}

/**
 * Start the win celebration animation.
 * Called once when the game transitions to Completed state.
 */
export function startWinAnimation(): void {
    if (_running) return

    // Skip animation entirely if animations are disabled (e.g. during tests)
    if (!appState.settings.animationsEnabled) return

    _running = true
    _startTime = performance.now()
    _cascadeCards = []
    _confettiParticles = []
    _lastLaunchTime = 0
    _lastConfettiBurst = 0
    _globalFrame = 0

    // Create offscreen canvas for permanent trail stamps
    const dpr = window.devicePixelRatio || 1
    _trailCanvas = document.createElement('canvas')
    _trailCanvas.width = appState.windowWidth * dpr
    _trailCanvas.height = appState.windowHeight * dpr
    _trailCtx = _trailCanvas.getContext('2d')
    if (_trailCtx) {
        _trailCtx.scale(dpr, dpr)
    }

    // Bob Ross: 1% chance
    _showBobRoss = Math.random() < 0.01

    // Build the card queue from foundations — King on top, Ace at bottom
    // Launch order: cycle through foundations, top card first (King → Queen → ... → Ace)
    _cardQueue = []
    const state = getCurrentCardState()
    const maxLen = Math.max(...state.foundation.map((f) => f.length))
    for (let i = maxLen - 1; i >= 0; i--) {
        for (let f = 0; f < 4; f++) {
            if (i < state.foundation[f].length) {
                _cardQueue.push({ card: state.foundation[f][i], foundationIndex: f })
            }
        }
    }

    // Start the RAF loop
    _rafId = requestAnimationFrame(tick)
}

/** Stop the win animation and clean up. Called when starting a new game. */
export function stopWinAnimation(): void {
    _running = false
    _finished = false
    if (_rafId !== null) {
        cancelAnimationFrame(_rafId)
        _rafId = null
    }
    _cascadeCards = []
    _trailCanvas = null
    _trailCtx = null
    _globalFrame = 0
    _cardQueue = []
    _confettiParticles = []
    _showBobRoss = false
}

// ─── Core loop ──────────────────────────────────────────────────────────────

function tick(): void {
    if (!_running) return

    const now = performance.now()
    const elapsed = now - _startTime

    // Launch queued cards at intervals
    if (_cardQueue.length > 0 && elapsed - _lastLaunchTime >= LAUNCH_INTERVAL) {
        launchNextCard()
        _lastLaunchTime = elapsed
    }

    // Spawn confetti bursts
    if (elapsed - _lastConfettiBurst >= CONFETTI_BURST_INTERVAL && elapsed < 6000) {
        spawnConfettiBurst()
        _lastConfettiBurst = elapsed
    }

    // Physics step
    updateCascadeCards()
    updateConfetti()

    // Increment frame counter (used for stamp spacing)
    _globalFrame++

    // Render
    const canvas = document.getElementById('canvas') as HTMLCanvasElement
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // Clear and redraw background
    ctx.clearRect(0, 0, appState.windowWidth, appState.windowHeight)
    ctx.fillStyle = appState.currentTheme.backgroundColor
    ctx.fillRect(0, 0, appState.windowWidth, appState.windowHeight)

    // Draw trail stamps (oldest first, so newest are on top)
    drawTrailStamps(ctx)

    // Draw active cascade cards
    drawCascadeCards(ctx)

    // Draw confetti
    drawConfetti(ctx)

    // Draw "You Win!" text with fade-in
    drawWinText(ctx, elapsed)

    // Draw completion UI on top of the animated background
    drawNewGameButton(ctx)
    drawRecordsToggleButton(ctx)
    drawCompletionTimer(ctx)

    // Keep drawRecords call active for diagnostics/e2e checks even when
    // the panel is hidden by default on the win screen.
    drawRecords(ctx)

    if (appState.showWinRecords) {
        drawCompletionBackdrop(ctx)
        drawCompletionTimer(ctx)
        drawRecords(ctx)
        drawNewGameButton(ctx)
        drawRecordsToggleButton(ctx)
    }

    // Continue while cards are still flying, queued, or confetti is active
    if (_cascadeCards.length > 0 || _cardQueue.length > 0 || _confettiParticles.length > 0) {
        _rafId = requestAnimationFrame(tick)
    } else {
        // Everything is gone — clean end state
        _running = false
        _finished = true
        _rafId = null
        schedulePaint()
    }
}

// ─── Card cascade ───────────────────────────────────────────────────────────

function launchNextCard(): void {
    const entry = _cardQueue.shift()
    if (!entry) return

    const { card, foundationIndex } = entry
    const startX = foundationCardX(foundationIndex)
    const startY = foundationCardY(foundationIndex)

    // Random horizontal velocity — spread cards widely across the screen
    const centerX = appState.windowWidth / 2
    const directionToCenter = centerX > startX + appState.cardWidth / 2 ? 1 : -1
    // Mix of center-biased and random direction for wide spread
    const randomDir = Math.random() < 0.3 ? -directionToCenter : directionToCenter
    const vx = randomDir * (3 + Math.random() * 5) + (Math.random() - 0.5) * 2

    // Initial upward velocity (negative = up) — moderate arc for good screen coverage
    const vy = -(5 + Math.random() * 5)

    // Slight random rotation
    const vr = (Math.random() - 0.5) * 0.04

    _cascadeCards.push({
        card,
        x: startX,
        y: startY,
        vx,
        vy,
        rotation: 0,
        vr,
        frameCount: 0,
    })
}

function updateCascadeCards(): void {
    const gravity = 0.25
    const bounceDamping = 0.75
    const trailInterval = 30 // leave a fading trail stamp every N frames
    const w = appState.windowWidth
    const h = appState.windowHeight
    const cw = appState.cardWidth
    const ch = appState.cardHeight

    const toRemove: number[] = []

    for (let i = 0; i < _cascadeCards.length; i++) {
        const cc = _cascadeCards[i]

        // Apply gravity
        cc.vy += gravity

        // Update position
        cc.x += cc.vx
        cc.y += cc.vy
        cc.rotation += cc.vr
        cc.frameCount++

        // Leave continuous trail stamps (classic Windows Solitaire smearing effect)
        if (cc.frameCount % trailInterval === 0) {
            stampTrailCard(cc.card, cc.x, cc.y) // permanent trail — builds up the painted screen
        }

        // Bounce off left/right walls
        if (cc.x < 0) {
            cc.x = 0
            cc.vx = Math.abs(cc.vx) * bounceDamping
            cc.vr = -cc.vr
        } else if (cc.x + cw > w) {
            cc.x = w - cw
            cc.vx = -Math.abs(cc.vx) * bounceDamping
            cc.vr = -cc.vr
        }

        // Bounce off bottom
        if (cc.y + ch > h) {
            cc.y = h - ch
            cc.vy = -Math.abs(cc.vy) * bounceDamping
            cc.vr *= 0.8

            // Leave a permanent stamp at each bounce point — builds up the classic layered look
            stampTrailCard(cc.card, cc.x, cc.y)

            // If barely bouncing, remove the card (it already got its permanent stamp above)
            if (Math.abs(cc.vy) < 1.5) {
                toRemove.push(i)
            }
        }

        // Remove if off screen (left/right after a wild bounce, or somehow up)
        if (cc.y > h + ch * 2 || cc.x < -cw * 2 || cc.x > w + cw * 2) {
            toRemove.push(i)
        }
    }

    // Remove in reverse order to preserve indices
    for (let i = toRemove.length - 1; i >= 0; i--) {
        _cascadeCards.splice(toRemove[i], 1)
    }
}

/** Bake a trail stamp onto the offscreen canvas at the current position. */
function stampTrailCard(card: Card, x: number, y: number): void {
    if (_trailCtx) {
        _trailCtx.globalAlpha = 1
        drawCardAt(card, _trailCtx, x, y)
    }
}

/** Blit permanent stamps from offscreen buffer. All stamps are permanent now. */
function drawTrailStamps(ctx: CanvasRenderingContext2D): void {
    if (_trailCanvas) {
        ctx.drawImage(_trailCanvas, 0, 0, appState.windowWidth, appState.windowHeight)
    }
}

function drawCascadeCards(ctx: CanvasRenderingContext2D): void {
    for (const cc of _cascadeCards) {
        drawCardAt(cc.card, ctx, cc.x, cc.y)
    }
}

// ─── Confetti ───────────────────────────────────────────────────────────────

function spawnConfettiBurst(): void {
    const w = appState.windowWidth
    const h = appState.windowHeight
    const theme = appState.currentTheme

    // Confetti colors: mix of theme highlight, red/black card colors, and card face/back
    const colors = [
        theme.highlightColor,
        theme.redCardColor,
        theme.cardBackColor,
        theme.cardFaceColor,
        theme.textColor,
    ]

    // Spawn from both bottom corners
    for (const cornerX of [0, w]) {
        const count = 4 + Math.floor(Math.random() * 4)
        for (let i = 0; i < count; i++) {
            const dirX = cornerX === 0 ? 1 : -1
            _confettiParticles.push({
                x: cornerX + dirX * Math.random() * 30,
                y: h - Math.random() * 20,
                vx: dirX * (2 + Math.random() * 5),
                vy: -(6 + Math.random() * 8),
                width: 3 + Math.random() * 5,
                height: 2 + Math.random() * 4,
                color: colors[Math.floor(Math.random() * colors.length)],
                rotation: Math.random() * Math.PI * 2,
                vr: (Math.random() - 0.5) * 0.3,
                opacity: 1,
                gravity: 0.12 + Math.random() * 0.06,
            })
        }
    }
}

function updateConfetti(): void {
    const h = appState.windowHeight
    const toRemove: number[] = []

    for (let i = 0; i < _confettiParticles.length; i++) {
        const p = _confettiParticles[i]
        p.vy += p.gravity
        p.vx *= 0.99 // air resistance
        p.x += p.vx
        p.y += p.vy
        p.rotation += p.vr

        // Fade out as they fall past 70% of screen height
        if (p.y > h * 0.7) {
            p.opacity -= 0.02
        }

        // Remove if off screen or fully faded
        if (p.y > h + 20 || p.opacity <= 0) {
            toRemove.push(i)
        }
    }

    for (let i = toRemove.length - 1; i >= 0; i--) {
        _confettiParticles.splice(toRemove[i], 1)
    }
}

function drawConfetti(ctx: CanvasRenderingContext2D): void {
    for (const p of _confettiParticles) {
        ctx.save()
        ctx.translate(p.x, p.y)
        ctx.rotate(p.rotation)
        ctx.globalAlpha = Math.max(0, p.opacity)

        // Parse the color and apply it
        const [r, g, b] = parseColorToRGB(p.color)
        ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${p.opacity})`

        ctx.fillRect(-p.width / 2, -p.height / 2, p.width, p.height)
        ctx.restore()
    }
    ctx.globalAlpha = 1
}

// ─── Win text ───────────────────────────────────────────────────────────────

function drawWinText(ctx: CanvasRenderingContext2D, elapsed: number): void {
    if (elapsed < TEXT_FADE_START) return

    const fadeProgress = Math.min(1, (elapsed - TEXT_FADE_START) / TEXT_FADE_DURATION)

    const theme = appState.currentTheme
    const w = appState.windowWidth
    const h = appState.windowHeight
    const ch = appState.cardHeight

    // "You Win!" in the upper third of the screen
    const fontSize = Math.floor(ch * 0.6)
    const textY = Math.floor(h * 0.35)

    ctx.save()
    ctx.globalAlpha = fadeProgress
    ctx.font = `700 ${fontSize}px ${CARD_FONT}`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'

    // Measure text to draw a dark pill backdrop behind it
    const metrics = ctx.measureText('You Win!')
    const textWidth = metrics.width
    const paddingX = fontSize * 0.5
    const paddingY = fontSize * 0.35
    const pillX = w / 2 - textWidth / 2 - paddingX
    const pillY = textY - fontSize / 2 - paddingY
    const pillW = textWidth + paddingX * 2
    const pillH = fontSize + paddingY * 2
    const pillR = pillH / 2

    // Semi-transparent dark backdrop pill
    ctx.fillStyle = 'rgba(0, 0, 0, 0.55)'
    ctx.beginPath()
    ctx.roundRect(pillX, pillY, pillW, pillH, pillR)
    ctx.fill()

    // Main text in theme highlight color (no shadow needed with backdrop)
    ctx.fillStyle = theme.highlightColor
    ctx.fillText('You Win!', w / 2, textY)

    ctx.restore()
}
